import * as React from 'react';
import { DataGrid, GridRow } from '@mui/x-data-grid';
import { services } from '../pages/Sell/services';
import {
  Chip,
  Stack,
  Typography,
  useMediaQuery,
  Paper,
  Box,
  IconButton,
  Collapse,
  Tooltip,
  Divider,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TrendingDownIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingUpIcon from '@mui/icons-material/TrendingUpRounded';
import { useTheme } from '@mui/material/styles';

// ================= کامپوننت سطر کشویی بازشونده (طراحی خاکستری مشابه کامپوننت مبدا) =================
const CustomRow = React.forwardRef((props, ref) => {
  const { expandedRowId, ...otherProps } = props;
  const isExpanded = expandedRowId === props.rowId;
  const rawItems = props.row?.items || [];
  const stats = props.row?.buybackVolumeStats || props.row?.stats;

  // محاسبه اقلام تجمیعی بر اساس وزن
  const cumulativeItems = React.useMemo(() => {
    return rawItems.reduce((acc, curr) => {
      const weight = Number(curr.weight);
      const price = Number(curr.price || 0);
      const existing = acc.find(b => b.weight === weight);

      if (!existing) {
        acc.push({
          weight,
          count: 1,
          unitPrice: price,
          totalPrice: price,
        });
      } else {
        existing.count += 1;
        existing.totalPrice += price;
        existing.unitPrice = existing.totalPrice / existing.count;
      }
      return acc;
    }, []);
  }, [rawItems]);

  const totalWeight = rawItems.reduce(
    (sum, item) => sum + (Number(item.weight) || 0),
    0
  );
  const totalPrice = rawItems.reduce(
    (sum, item) => sum + (Number(item.price) || 0),
    0
  );
  const avgGramPrice =
    totalWeight > 0 ? Math.round(totalPrice / totalWeight) : 0;

  const isNegative = stats
    ? Number(stats.percentageDifferenceWithESTJT) < 0
    : false;

  return (
    <Box
      ref={ref}
      {...otherProps}
      sx={{
        ...props.style,
        height: 'auto !important',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isExpanded ? '#f8fafc' : 'inherit',
        transition: 'background-color 0.15s ease',
      }}
    >
      {/* سطر اصلی DataGrid */}
      <GridRow
        {...otherProps}
        style={{ width: '100%', position: 'relative' }}
      />

      {/* بخش کشویی: رنگ خاکستری خنثی دقیقاً مشابه کامپوننت مرجع */}
      <Collapse in={isExpanded} timeout={180} unmountOnExit>
        <Box
          sx={{
            width: '100%',
            p: 1.5,
            bgcolor: '#f8fafc',
            borderTop: '1px dashed #cbd5e1',
            borderBottom: '2px solid #94a3b8',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 1.5,
              alignItems: 'stretch',
            }}
          >
            {/* ================= بخش ۱: آمار و مظنه‌ها ================= */}
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {stats ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: '#f8fafc',
                      borderRadius: 1.5,
                      border: '1px solid #f1f5f9',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="#64748b"
                      display="block"
                      fontSize="0.7rem"
                    >
                      قیمت گرم بازخرید
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="#0f172a"
                      fontSize="0.8rem"
                      mt={0.3}
                    >
                      {Number(stats.buyPrice || avgGramPrice).toLocaleString(
                        'fa-IR'
                      )}{' '}
                      ریال
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1,
                      bgcolor: '#f8fafc',
                      borderRadius: 1.5,
                      border: '1px solid #f1f5f9',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="#64748b"
                      display="block"
                      fontSize="0.7rem"
                    >
                      قیمت گرم مرجع
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="#0f172a"
                      fontSize="0.8rem"
                      mt={0.3}
                    >
                      {Number(stats.ESTJTPrice || 0).toLocaleString('fa-IR')}{' '}
                      ریال
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1,
                      bgcolor: '#f8fafc',
                      borderRadius: 1.5,
                      border: '1px solid #f1f5f9',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="#64748b"
                      display="block"
                      fontSize="0.7rem"
                    >
                      قیمت مثقال مرجع
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="#334155"
                      fontSize="0.8rem"
                      mt={0.3}
                    >
                      {Number(stats.methghalPrice || 0).toLocaleString('fa-IR')}{' '}
                      ریال
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1,
                      bgcolor: '#f8fafc',
                      borderRadius: 1.5,
                      border: '1px solid #f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="#64748b"
                        display="block"
                        fontSize="0.7rem"
                      >
                        اختلاف با مرجع
                      </Typography>
                      <Chip
                        size="small"
                        icon={
                          isNegative ? (
                            <TrendingDownIcon
                              sx={{ '&&': { color: '#dc2626' }, fontSize: 13 }}
                            />
                          ) : (
                            <TrendingUpIcon
                              sx={{ '&&': { color: '#16a34a' }, fontSize: 13 }}
                            />
                          )
                        }
                        label={`${stats.percentageDifferenceWithESTJT || 0}٪`}
                        sx={{
                          height: 22,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          borderRadius: '4px',
                          bgcolor: isNegative ? '#fef2f2' : '#f0fdf4',
                          color: isNegative ? '#991b1b' : '#166534',
                          border: `1px solid ${isNegative ? '#fecaca' : '#bbf7d0'}`,
                          px: 0.3,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    // gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: '#f8fafc',
                      borderRadius: 1.5,
                      border: '1px solid #f1f5f9',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="#64748b"
                      display="block"
                      fontSize="0.7rem"
                    >
                      قیمت هر گرم خرید ما
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="#0f172a"
                      fontSize="0.8rem"
                      mt={0.3}
                    >
                      {avgGramPrice.toLocaleString('fa-IR')} ریال
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>

            {/* ================= بخش ۲: تفکیک شمش‌ها ================= */}
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: cumulativeItems.length > 1 ? 'repeat(2, 1fr)' : '1fr',
                  },
                  gap: 1,
                  maxHeight: 130,
                  overflowY: 'auto',
                  pr: 0.5,
                }}
              >
                {cumulativeItems
                  .slice()
                  .sort((a, b) => a.weight - b.weight)
                  .map(item => (
                    <Paper
                      key={item.weight}
                      elevation={0}
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        border: '1px solid #e2e8f0',
                        bgcolor: '#f8fafc',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: '#ffffff',
                          borderColor: '#cbd5e1',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        },
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="#0f172a"
                          fontSize="0.76rem"
                        >
                          شمش {Number(item.weight).toLocaleString('fa-IR')} گرمی
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            bgcolor: '#ffffff',
                            border: '1px solid #cbd5e1',
                            color: '#334155',
                            fontWeight: 700,
                            px: 0.8,
                            py: 0.1,
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                          }}
                        >
                          {Number(item.count).toLocaleString('fa-IR')} عدد
                        </Typography>
                      </Box>

                      <Divider sx={{ borderStyle: 'dashed', my: 0.2 }} />

                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          variant="caption"
                          color="#64748b"
                          fontSize="0.7rem"
                        >
                          فی:{' '}
                          {Math.round(item.unitPrice).toLocaleString('fa-IR')}{' '}
                          ریال
                        </Typography>
                        {item.count > 1 && (
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            color="#0f172a"
                            fontSize="0.72rem"
                          >
                            کل: {item.totalPrice.toLocaleString('fa-IR')}
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  ))}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
});

export default function BuybackTable({ trigger }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [rows, setRows] = React.useState([]);
  const [expandedRowId, setExpandedRowId] = React.useState(null);

  React.useEffect(() => {
    services.getBuybackHistory().then(data => {
      const sortedData = (data?.data || []).slice().sort(compare);
      setRows(sortedData);
    });
  }, [trigger]);

  const columns = React.useMemo(
    () => [
      // ۱. دکمه باز/بستن کشو
      {
        field: 'expand',
        headerName: '',
        sortable: false,
        filterable: false,
        width: 44,
        renderCell: params => {
          const isExpanded = expandedRowId === params.id;
          return (
            <Tooltip title={isExpanded ? 'بستن جزئیات' : 'مشاهده اقلام و آمار'}>
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setExpandedRowId(prev =>
                    prev === params.id ? null : params.id
                  );
                }}
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: isExpanded ? '#f1f5f9' : 'transparent',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  '&:hover': { bgcolor: '#e2e8f0' },
                }}
              >
                <KeyboardArrowDownIcon
                  sx={{
                    color: isExpanded ? '#ea580c' : '#64748b',
                    fontSize: 20,
                  }}
                />
              </IconButton>
            </Tooltip>
          );
        },
      },
      // ۲. مشتری
      {
        field: 'customer_name',
        sortable: false,
        headerName: 'مشتری',
        minWidth: 140,
        flex: 0.8,
        renderCell: params => (
          <Stack spacing={0.2} justifyContent="center" height="100%">
            <Typography
              variant="body2"
              fontWeight={600}
              color="#0f172a"
              fontSize="0.82rem"
            >
              {params.value}
            </Typography>
            <Typography variant="caption" color="#64748b" fontSize="0.72rem">
              کد ملی: {params.row.national_id || '—'}
            </Typography>
          </Stack>
        ),
      },
      // ۳. تاریخ
      {
        field: 'datetime',
        sortable: false,
        filterable: false,
        headerName: 'تاریخ',
        minWidth: 120,
        flex: 0.6,
        renderCell: params => (
          <Typography variant="body2" color="text.secondary">
            {new Date(params.value || params.row.createdAt).toLocaleString(
              'fa-IR'
            )}
          </Typography>
        ),
      },
      // ۴. شعبه
      {
        field: 'location',
        sortable: false,
        filterable: false,
        headerName: 'شعبه',
        minWidth: 75,
        flex: 0.4,
        renderCell: params => (
          <Typography variant="body2">شعبه {params.value}</Typography>
        ),
        hide: isMobile,
      },
      // ۵. خلاصه سبد خرید
      {
        field: 'items',
        sortable: false,
        filterable: false,
        headerName: 'خلاصه سبد خرید',
        minWidth: 170,
        flex: 1,
        renderCell: params => {
          const items = params.value || [];
          const summary = items.reduce(
            (acc, curr) => ({
              totalWeight: acc.totalWeight + (Number(curr.weight) || 0),
              totalPrice: acc.totalPrice + (Number(curr.price) || 0),
            }),
            { totalWeight: 0, totalPrice: 0 }
          );

          return (
            <Chip
              sx={{
                backgroundColor: '#fff7ed',
                borderRadius: 2,
                height: 38,
                border: '1px solid #fed7aa',
              }}
              label={
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Stack direction="row" spacing={0.4} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      وزن:{' '}
                      <Typography
                        component="span"
                        variant="caption"
                        fontWeight={700}
                        color="#ea580c"
                      >
                        {Number(summary.totalWeight).toLocaleString('fa-IR')}{' '}
                        گرم
                      </Typography>
                    </Typography>
                  </Stack>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: 12, my: 'auto', borderColor: '#fed7aa' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    مبلغ:{' '}
                    <Typography
                      component="span"
                      variant="caption"
                      fontWeight={700}
                      color="#ea580c"
                    >
                      {Number(summary.totalPrice).toLocaleString('fa-IR')} ریال
                    </Typography>
                  </Typography>
                </Stack>
              }
            />
          );
        },
      },
    ],
    [isMobile, expandedRowId]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid #fed7aa',
        backgroundColor: '#fff',
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.05)',
      }}
    >
      <div
        style={{
          height: isMobile ? 'auto' : 580,
          width: '100%',
          direction: 'rtl',
          overflowX: 'auto',
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSizeOptions={[5, 10, 25]}
          rowHeight={isMobile ? 54 : 62}
          getRowHeight={params =>
            expandedRowId === params.id ? 'auto' : isMobile ? 54 : 62
          }
          slots={{
            row: CustomRow,
          }}
          slotProps={{
            row: {
              expandedRowId,
            },
          }}
          autoHeight={isMobile}
          disableColumnMenu
          sx={{
            border: 'none',
            fontFamily: theme.typography.fontFamily,
            fontVariantNumeric: 'tabular-nums',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#fff7ed',
              borderBottom: '1px solid #fed7aa',
              fontSize: '0.82rem',
              fontWeight: 600,
              color: '#9a3412',
              '& .MuiDataGrid-columnHeader': {
                padding: isMobile ? '0 6px' : '0 12px',
              },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f1f5f9',
              padding: isMobile ? '4px 6px' : '6px 12px',
              fontSize: '0.82rem',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#fffaf5',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid #fed7aa',
              backgroundColor: '#fafcff',
            },
            '& .MuiTablePagination-root': {
              color: '#9a3412',
            },
            '& .MuiTablePagination-actions button': {
              color: '#ea580c',
            },
          }}
        />
      </div>
    </Paper>
  );
}

function compare(a, b) {
  const dateA = new Date(a.createdAt || a.datetime);
  const dateB = new Date(b.createdAt || b.datetime);
  if (dateA < dateB) return 1;
  if (dateA > dateB) return -1;
  return 0;
}
